import { DataLoadingWrapper, EmptyListContent } from "@jugl-web/ui-components";
import React, { useMemo } from "react";
import subMonths from "date-fns/subMonths";
import {
  AIAgentInsights,
  CRMChannel,
  useRestApiProvider,
} from "@jugl-web/rest-api";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { useTranslations } from "@jugl-web/utils";
import { Lottie } from "@jugl-web/utils/utils/Lottie";
import { ReactComponent as InstagramIcon } from "./assets/instagram.svg";
import { ReactComponent as WebChatIcon } from "./assets/web-chat.svg";
import { ReactComponent as WhatsappIcon } from "./assets/whatsapp.svg";
import { ReactComponent as TwilioIcon } from "./assets/twilio.svg";
import { ReactComponent as AIIcon } from "./assets/ai.svg";
import { ReactComponent as PortraitIcon } from "./assets/portrait.svg";
import { ReactComponent as HealingIcon } from "./assets/healing.svg";
import { ReactComponent as ArrowIcon } from "./assets/arrow.svg";
import { ReactComponent as LineIcon } from "./assets/line.svg";
import { CRMDrawerTabsLayout } from "../../components/CRMDrawerTabsLayout";
import emptyAnimation from "./assets/empty-animation.json";
import { InisghtsCard } from "./components/InisghtsCard";

export const AIAgentInsightsScreen: React.FC = () => {
  const { integrationsApi } = useRestApiProvider();
  const { entityId } = useEntitySelectedProvider();
  const { t } = useTranslations();

  const timeRange = useMemo(() => {
    const now = new Date();

    return {
      start_time: subMonths(now, 6).toISOString(),
      end_time: now.toISOString(),
    };
  }, []);

  // WHATSAPP
  const {
    data: whatsappAIInsights,
    isLoading: isWhatsappAIInsightsLoading,
    isFetching: isWhatsappAIInsightsFetching,
    isError: isWhatsappAIInsightsError,
    refetch: whatsappAIInsightsRefetch,
  } = integrationsApi.useGetAIAgentInsightsQuery({
    entityId,
    channel: CRMChannel.whatsapp,
    ...timeRange,
  });

  // INSTAGRAM
  const {
    data: instagramAIInsights,
    isLoading: isInstagramAIInsightsLoading,
    isFetching: isInstagramAIInsightsFetching,
    isError: isInstagramAIInsightsError,
    refetch: instagramAIInsightsRefetch,
  } = integrationsApi.useGetAIAgentInsightsQuery({
    entityId,
    channel: CRMChannel.instagram,
    ...timeRange,
  });

  // CHATBOT
  const {
    data: chatbotAIInsights,
    isLoading: isChatbotAIInsightsLoading,
    isFetching: isChatbotAIInsightsFetching,
    isError: isChatbotAIInsightsError,
    refetch: chatbotAIInsightsRefetch,
  } = integrationsApi.useGetAIAgentInsightsQuery({
    entityId,
    channel: CRMChannel.chatbot,
    ...timeRange,
  });

  // TWILIO
  const {
    data: twilioAIInsights,
    isLoading: isTwilioAIInsightsLoading,
    isFetching: isTwilioAIInsightsFetching,
    isError: isTwilioAIInsightsError,
    refetch: twilioAIInsightsRefetch,
  } = integrationsApi.useGetAIAgentInsightsQuery({
    entityId,
    channel: CRMChannel.twilio,
    ...timeRange,
  });

  const totalInsights = useMemo<Pick<
    AIAgentInsights,
    | "resolved"
    | "unresolved"
    | "total_ai_conversations"
    | "ai_replies"
    | "human_sent_messages"
  > | null>(() => {
    const insightsByChannel: Record<CRMChannel, AIAgentInsights | undefined> = {
      whatsapp: whatsappAIInsights,
      instagram: instagramAIInsights,
      chatbot: chatbotAIInsights,
      twilio: twilioAIInsights,
    };

    const items = Object.values(insightsByChannel);
    if (items.some((x) => x == null)) return null;

    const validItems = items as AIAgentInsights[];

    return validItems.reduce(
      (acc, item) => ({
        resolved: acc.resolved + item.resolved,
        unresolved: acc.unresolved + item.unresolved,
        total_ai_conversations:
          acc.total_ai_conversations + item.total_ai_conversations,
        ai_replies: acc.ai_replies + item.ai_replies,
        human_sent_messages: acc.human_sent_messages + item.human_sent_messages,
      }),
      {
        resolved: 0,
        unresolved: 0,
        total_ai_conversations: 0,
        ai_replies: 0,
        human_sent_messages: 0,
      }
    );
  }, [
    whatsappAIInsights,
    instagramAIInsights,
    chatbotAIInsights,
    twilioAIInsights,
  ]);

  return (
    <CRMDrawerTabsLayout>
      <DataLoadingWrapper
        isLoading={
          isWhatsappAIInsightsLoading ||
          isWhatsappAIInsightsFetching ||
          isInstagramAIInsightsLoading ||
          isInstagramAIInsightsFetching ||
          isChatbotAIInsightsLoading ||
          isChatbotAIInsightsFetching ||
          isTwilioAIInsightsLoading ||
          isTwilioAIInsightsFetching
        }
        isError={
          isWhatsappAIInsightsError ||
          isInstagramAIInsightsError ||
          isChatbotAIInsightsError ||
          isTwilioAIInsightsError
        }
        onRetry={() => {
          if (isWhatsappAIInsightsError) {
            whatsappAIInsightsRefetch();
          }
          if (isInstagramAIInsightsError) {
            instagramAIInsightsRefetch();
          }
          if (isChatbotAIInsightsError) {
            chatbotAIInsightsRefetch();
          }
          if (isTwilioAIInsightsError) {
            twilioAIInsightsRefetch();
          }
        }}
      >
        {(() => {
          if (
            !whatsappAIInsights ||
            !instagramAIInsights ||
            !chatbotAIInsights ||
            !twilioAIInsights ||
            !totalInsights
          ) {
            return null;
          }

          if (totalInsights.total_ai_conversations === 0) {
            return (
              <EmptyListContent
                className="w-[500px]"
                type="custom"
                customImg={
                  <Lottie
                    options={{ animationData: emptyAnimation }}
                    width={221}
                    height={166}
                  />
                }
                customTitle={t({
                  id: "client-support-page.ai-agent-insights-is-empty",
                  defaultMessage: "AI Agent Insights is empty yet",
                })}
                customSubtitle={t({
                  id: "client-support-page.overwiew-ai-agent-performance-will-be-shown-here",
                  defaultMessage:
                    "Overview of AI Agent performance will be shown here",
                })}
              />
            );
          }

          return (
            <div className="flex min-h-min min-w-min flex-col gap-12 py-20 px-10">
              <div className="flex flex-col items-center gap-2 text-center">
                <span className="font-secondary text-2xl font-medium text-black">
                  {t({
                    id: "client-support-page.ai-performance-insights",
                    defaultMessage: "AI Performance Insights",
                  })}
                </span>
                <span className="font-secondary text-[#828282]">
                  {t({
                    id: "client-support-page.overwiew-ai-agent-performance",
                    defaultMessage:
                      "Overview of AI Agent performance based on last 6 month ✨",
                  })}
                </span>
              </div>
              <div className="flex justify-center gap-[78px]">
                <div className="flex basis-1 flex-col gap-8">
                  <InisghtsCard
                    icon={WhatsappIcon}
                    title={t({
                      id: "client-support-page.whatsapp",
                      defaultMessage: "WhatsApp",
                    })}
                    conversations={whatsappAIInsights.total_conversations}
                    messages={
                      whatsappAIInsights.ai_replies +
                      whatsappAIInsights.human_sent_messages
                    }
                    backgroundColor="bg-[#EDF7EE]"
                  />
                  <InisghtsCard
                    icon={InstagramIcon}
                    title={t({
                      id: "client-support-page.instagram",
                      defaultMessage: "Instagram",
                    })}
                    conversations={instagramAIInsights.total_conversations}
                    messages={
                      instagramAIInsights.ai_replies +
                      instagramAIInsights.human_sent_messages
                    }
                    backgroundColor="bg-[#FFF1F5]"
                  />
                  <InisghtsCard
                    icon={WebChatIcon}
                    title={t({
                      id: "client-support-page.web-chat",
                      defaultMessage: "Web Chat",
                    })}
                    conversations={chatbotAIInsights.total_conversations}
                    messages={
                      chatbotAIInsights.ai_replies +
                      chatbotAIInsights.human_sent_messages
                    }
                    backgroundColor="bg-[#F1F7FF]"
                  />
                  <InisghtsCard
                    icon={TwilioIcon}
                    title={t({
                      id: "client-support-page.text-sms-chat",
                      defaultMessage: "Text (SMS) Chat",
                    })}
                    conversations={twilioAIInsights.total_conversations}
                    messages={
                      twilioAIInsights.ai_replies +
                      twilioAIInsights.human_sent_messages
                    }
                    backgroundColor="bg-[#EAFAF5]"
                  />
                </div>
                <div className="relative flex">
                  <div className="flex min-w-[300px] max-w-[300px] grow flex-col items-center justify-center gap-4 rounded-[48px] bg-[linear-gradient(226deg,#FFD8F8_0%,#F4F8FF_52.4%,#E4F3FF_100%)] p-8 text-center">
                    <AIIcon className="h-[57px] w-[57px] shrink-0" />
                    <div className="flex flex-col gap-2">
                      <span className="font-secondary font-semibold leading-[140%] text-black">
                        {t({
                          id: "client-support-page.ai-agent-involved",
                          defaultMessage: "Ai Agent involved",
                        })}
                      </span>
                      <span className="text-dark-700 font-secondary text-sm leading-[140%]">
                        {t(
                          {
                            id: "client-support-page.total-conversations-managed-by-ai-count",
                            defaultMessage:
                              "Total {conversationCount} {conversationCount, plural, one {Conversation} other {Conversations}} and {messageCount} {messageCount, plural, one {message} other {messages}} received",
                          },
                          {
                            conversationCount:
                              totalInsights.total_ai_conversations,
                            messageCount:
                              totalInsights.ai_replies +
                              totalInsights.human_sent_messages,
                          }
                        )}
                      </span>
                    </div>
                  </div>
                  <ArrowIcon className="absolute left-0 bottom-1/2 -translate-x-full -translate-y-[-4px]" />
                  <ArrowIcon className="absolute left-0 top-1/2 -translate-x-full -translate-y-[4px] scale-y-[-1]" />
                  <LineIcon className="absolute -right-[78px] top-1/2 -translate-y-1/2" />
                </div>
                <div className="flex basis-1 flex-col gap-8">
                  <InisghtsCard
                    icon={HealingIcon}
                    title={t({
                      id: "client-support-page.managed-ai-agent",
                      defaultMessage: "Managed by AI Agent",
                    })}
                    conversations={totalInsights.resolved}
                    messages={totalInsights.ai_replies}
                    backgroundColor="bg-[linear-gradient(180deg,#EBFFE7_0%,#ECFCFF_100%)]"
                    orientation="column"
                  />
                  <InisghtsCard
                    icon={PortraitIcon}
                    title={t({
                      id: "client-support-page.transferred-to-human",
                      defaultMessage: "Transferred to Human",
                    })}
                    conversations={totalInsights.unresolved}
                    messages={totalInsights.human_sent_messages}
                    backgroundColor="bg-[linear-gradient(180deg,#FFF8E6_0%,#FFF1F9_100%)]"
                    orientation="column"
                  />
                </div>
              </div>
            </div>
          );
        })()}
      </DataLoadingWrapper>
    </CRMDrawerTabsLayout>
  );
};
